/* eslint-disable import/no-anonymous-default-export */
import { Button, Form, Modal, Progress, Select, Spin, Tooltip } from "antd";
import { createContext, useEffect, useState } from "react";
import styled from "styled-components";
import { ActionResult, Query } from "../../common/hooks/useApi";
import { CaretRightOutlined, CaretLeftOutlined, RightOutlined } from '@ant-design/icons';
import { Infraccion, InfraccionAgrupada, InfraccionGroupType } from "../hooks/useInfraccionesAPI";
import useTaskQueue, { TaskQueue } from "../../common/hooks/useTaskQueue";
import useForceUpdate from "../../common/hooks/useForceUpdate";
import { useNavigate, useParams } from "react-router-dom";
import { EstadisticaData } from "../../estadisticas/useEstadisticasAPI";
import moment from "moment-business-days";
import EstadisticasPage from "../../estadisticas/EstadisticasPage";
import EstadisticasContent, { Proceso } from "../../estadisticas/components/EstadisticasContent";

const Container = styled.div``;

const InfraccionContainer = styled.div``;

const QuickStatistics = styled.div`
display:flex;
gap:1rem;
& > div:not(:nth-child(3))
{
    display:flex;
    font-size: 0.8rem;
    & > :first-child
    {
        background: #424242;
        color: white;
        padding: 0.5rem;
    }
    & > :nth-child(2)
    {
        display: flex;
        align-items: center;
        padding: 0.5rem;
        background: white;
    }
}

`;

const Header = styled.div`
padding-bottom:1rem;
margin-bottom:1rem;
border-bottom:1px solid #9e9e9e;
font-size: 1.25rem;
font-weight: 600;
display: flex;
justify-content: space-between;
align-items: center;

`;

const Body = styled.div`
position:relative;
& > ${InfraccionContainer}
{
    display:none;
}
& > ${InfraccionContainer}.visible
{
    display:block;
}
`;

const StyledSpin = styled(Spin)`
position: absolute;
z-index: 1;
width: 100%;
background: rgba(0,0,0,0.45);
height: 100%;
display: flex;
align-items: center;
justify-content: center;
`;

const StyledSpinFullscreen = styled(StyledSpin)`
position:fixed;
left:0;
top:0;
z-index:2;
`;

const PaginationContainer = styled.div`
display: flex;
background: #bdbdbd;
padding: 0.5rem;
grid-gap: 0.5rem;
justify-content: center;
align-items: center;
`;

const PaginationItems = styled.div`
display: flex;
grid-gap: 0.25rem;
align-items: center;
overflow:auto;
`;

const PaginationItem = styled.div<{ selected?: boolean }>`
height: 30px;
width:30px;
border:${props => props.selected ? '3px solid' : 'none'};
cursor:pointer;
`;

const EmptyMessage = styled.p`
text-align: center;
padding: 2rem;
background: white;
font-weight: 600;
`;

const StyledItemSpin = styled(Spin)`
display: flex;
align-items: center;
justify-content: center;
padding-left: 0.5rem;
padding-right: 0.5rem;
`;

const FoldersContainer = styled.div`
padding: 0.5rem;
background: white;
display: grid;
grid-template-columns:repeat(auto-fill,180px);
grid-auto-rows: 160px;
align-items:center;
gap: 0.5rem;
`;

const FolderContainer = styled.div<{ selected?: boolean }>`
height:100%;
flex-wrap: wrap;
padding: 0.75rem;
background: #FFE9A2;
font-weight: 600;
box-shadow: rgb(99 99 99 / 20%) 0px 2px 8px 0px;
border:${props => props.selected ? '3px solid' : 'none'};
display: flex;
align-items: center;
padding-left: 1rem;
text-align: center;
justify-content: center;
line-height: 1.3;
cursor:pointer;
transition:all 0.2s;
&:hover
{
    box-shadow:none;
    background:#FFE4B5;
}
& > :first-child
{
    text-overflow: ellipsis;
    overflow: hidden;
    display: -webkit-box !important;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    white-space: normal;
}
& > :last-child
{
    font-weight:400;
    display:grid;
    & > :last-child
    {
        font-size: 0.85rem;
        font-weight: 700;
    }
}
`;

const Breadcrumb = styled.div`
display: flex;
gap: 0.5rem;
font-weight: 600;
background: #212121;
padding: 0.5rem;
color: white;
& > div
{
    cursor:pointer;
}
& > div:last-child > span{
    display:none;
}
`;

const Filters = styled(Form)`
display: grid;
grid-template-columns: repeat(auto-fill,minmax(250px, 1fr));
`;


interface InfraccionesBrowserProps {
    children: React.ReactNode
    title: string
    list: (top: number, skip: number, filter: string[]) => Promise<ActionResult<Infraccion[]>>
    findById: (id: number) => Promise<ActionResult<Infraccion[]>>
    groupBy?: (query: Query, groupType: InfraccionGroupType) => Promise<ActionResult<InfraccionAgrupada[]>>
    defaultFilter?: string[]
    breadcrumbUrl?: string
    limit: number
    getItemStatusColor: (currentIndex: number, currentPage: number, infraccion: Infraccion) => string
    getProgress?: (data: InfraccionAgrupada) => number
    getProgressQuantity?: (data: InfraccionAgrupada) => string
    maxImagesPreload?: number

    todayStatisticsText?: string
    yesterdayStatisticsText?: string
    getMonthStatistics?: (month: number) => Promise<ActionResult<EstadisticaData[]>>
    statisticsProcess?: Proceso
    /**
     * Filtros personalizados para las infracciones y grupos de infracciones
     */
    statusFilters?:
    {
        /**
         * Nombre del filtro. Ej: 'Descartadas'
         */
        label: string,
        /**
         * Query string de filtro para el grupo de infracciones. Ej: CantidadInfraccionesDescartadas ge 1
         */
        groupFilter: string
        /**
         * Query string de filtro para las infracciones. Ej: Estado eq 'DESCARTADA'
         */
        infractionFilter: string
    }[]
};

export interface InfraccionesBrowserContextProps {
    infraccion: Infraccion,
    loadInfracciones: (opts?: { p?: number, filter?: [] }) => Promise<void>
    setLoadingCurrentIndex: (loading: boolean) => void
    refreshInfraccion: (index?: number) => any
    queue: TaskQueue
    goBackward: () => Promise<void>
    goForward: () => Promise<void>
    incrementTodayStatisticsValue: () => any
}

export interface FilterOptions {
    equipo?: { nombre: string, unidadProduccion: string },
    year?: number,
    month?: number,
    day?: number
};


export const InfraccionesBrowserContext = createContext<InfraccionesBrowserContextProps>(null!);

export const Folder = (props: {
    progress?: number
    progressQuantity?: string
    quantity: number
    title: string
    url: string
}) => {
    const navigate = useNavigate();
    return (
        <FolderContainer onClick={() => navigate(props.url)}>
            <span>
                <Tooltip placement="top" title={props.title}>
                    {props.title}
                </Tooltip>
            </span>
            {props.progress != undefined && <Progress showInfo={false} percent={props.progress} />}
            <div>
                {props.progress != undefined && <span>{props.progress}%</span>}
                {props.progressQuantity != undefined && <span>{props.progressQuantity}</span>}
                <span>{props.quantity} {props.quantity == 1 ? `infracción` : 'infracciones'}</span>
            </div>
        </FolderContainer>
    );
}

export default ({ maxImagesPreload = 20, ...props }: InfraccionesBrowserProps) => {
    const [index, setIndex] = useState(0);
    const [response, setResponse] = useState<ActionResult<Infraccion[]>>();
    const [groupResponse, setGroupResponse] = useState<ActionResult<InfraccionAgrupada[]>>();
    const [infracciones, setInfracciones] = useState<Infraccion[]>();
    const [page, setPage] = useState(1);
    const [filter, setFilter] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingIndexes, setLoadingIndexes] = useState<{ [key: number]: boolean }>({});
    const forceUpdate = useForceUpdate();
    const queue = useTaskQueue();
    const [equipo, setEquipo] = useState<{ nombre: string, unidadProduccion: string }>();
    const [year, setYear] = useState<number>();
    const [month, setMonth] = useState<number>();
    const [day, setDay] = useState<number>();
    const [todayStatisticsNumber, setTodayStatisticsNumber] = useState<number>();
    const [yesterdayStatisticsNumber, setYesterdayStatisticsNumber] = useState<number>();
    const [showStatistics, setShowStatistics] = useState(false);
    const [selectedFilter, setSelectedFilter] = useState<string>();
    const params = useParams<{ unidadProduccion: string, equipo: string, year: string, month: string, day: string }>();
    const navigate = useNavigate();
    const [lugarInfreaccion, setLugarInfraccion] = useState<string>('');
    useEffect(() => {
        if (props.getMonthStatistics) loadQuickStatistics();
    }, []);

    useEffect(() => {
        setIndex(0);
    }, [day])

    const loadQuickStatistics = async () => {

        const today = moment();
        const yesterday = moment().subtract(1, 'days');

        let statistics = await props.getMonthStatistics?.(parseInt(today.format("M")));
        setTodayStatisticsNumber(statistics?.data?.value.filter(s => s.key == parseInt(today.format("D"))).at(0)?.value ?? 0);
        if (today.format("M") != yesterday.format("M")) statistics = await props.getMonthStatistics?.(parseInt(yesterday.format("M")));
        setYesterdayStatisticsNumber(statistics?.data?.value.filter(s => s.key == parseInt(yesterday.format("D"))).at(0)?.value ?? 0);

    };

    const loadInfraccionesGroup = async (options?: FilterOptions) => {
        setLoading(true);
        if (options?.day) {
            await loadInfracciones({
                filter:
                    [
                        `Equipo/Nombre eq '${options.equipo?.nombre}'`,
                        `Equipo/ConfiguracionProduccion/UnidadProduccion/Nombre eq '${options.equipo?.unidadProduccion}'`,
                        `(year(FechaHora) eq ${options.year} and month(FechaHora) eq ${options.month} and day(FechaHora) eq ${options.day} or (FechaHora eq null and year(CreatedAt) eq ${options.year} and month(CreatedAt) eq ${options.month} and day(CreatedAt) eq ${options.day}))`,
                        selectedFilter ? props.statusFilters?.filter(f => f.label == selectedFilter).at(0)?.infractionFilter ?? '' : ''
                    ].filter(g => g != "")

                //(FechaHora eq null and year(CreatedAt) eq ${options.year})
            });
        }
        else {
            const response = await props.groupBy!({
                $orderby: [
                    !options?.equipo ? 'EquipoNombre desc, EquipoUnidadProduccion desc' : '',
                    options?.equipo ? 'Anio asc' : '',
                    options?.year ? 'Mes asc' : '',
                    options?.month ? 'Dia asc' : '',
                ].filter(g => g != ""),
                $filter: [
                    ...(props.defaultFilter ?? []),
                    options?.equipo ? `EquipoUnidadProduccion eq '${options.equipo.unidadProduccion}' and EquipoNombre eq '${options.equipo.nombre}'` : '',
                    options?.year ? `Anio eq ${options.year}` : "",
                    options?.month ? `Mes eq ${options.month}` : "",
                    selectedFilter ? props.statusFilters?.filter(f => f.label == selectedFilter).at(0)?.groupFilter ?? '' : ''
                ].filter(f => f != "")
            },
                ([
                    !options?.equipo ? "EQUIPO" : undefined,
                    options?.equipo ? "YEAR" : undefined,
                    options?.year ? "MONTH" : undefined,
                    options?.month ? "DAY" : undefined
                ] as (InfraccionGroupType | undefined)[]).filter(g => g != undefined).at(-1)!
            );
            setGroupResponse(response);
        }
        setEquipo(options?.equipo);
        setYear(options?.year);
        setMonth(options?.month);
        setDay(options?.day);

        setLoading(false);

    }

    const loadInfracciones = async (opts?: { p?: number, filter?: string[] }) => {

        setLoading(true);
        const response = await props.list(props.limit, ((opts?.p ?? page) - 1) * props.limit, opts?.filter ?? filter);

        if (index > 0 && !response.data?.value[index]) {
            setIndex(index - 1);
        }
        setResponse(response);

        setFilter(opts?.filter ?? filter);
        setPage(opts?.p ?? page);
        setInfracciones(response.data?.value);
        console.log(response.data?.value);
        setLugarInfraccion(response.data?.value[index]?.Lugar ?? '');
        setLoading(false);
    };

    const getTotalPages = () => {
        const count = response?.data?.["@odata.count"] ?? 0;
        return count > 0 ? Math.ceil(count / props.limit) : 0;
    };

    const getGlobalIndex = () => {
        return ((page - 1) * props.limit) + index + 1;
    };

    const goBackward = async (navigatePage?: boolean) => {

        if (loading) {
            return;
        }

        if (index > 0 && !navigatePage) {
            setIndex(index - 1);
        }
        else if (page > 1) {
            await loadInfracciones({ p: page > 1 ? page - 1 : 1 });
            setIndex(props.limit - 1);
        }
    };

    const goForward = async (navigatePage?: boolean) => {
        if (loading) {
            return;
        }

        if (index + 1 < response!.data!.value.length && !navigatePage) {
            setIndex(index + 1);
        }
        else {
            if (page + 1 <= getTotalPages()) {
                await loadInfracciones({ p: page + 1 });
                setIndex(0);
            }

        }
    };

    const setLoadingCurrentIndex = (loading: boolean) => {
        setLoadingIndexes({ ...loadingIndexes, ...{ [index]: loading } });
    }

    const refreshInfraccion = async (i?: number) => {
        i = i ?? index;
        if (!infracciones) {
            return;
        }

        loadingIndexes[i] = true;
        forceUpdate();
        infracciones[i] = (await props.findById(infracciones[i].Id)).data!.value[0];
        loadingIndexes[i] = false;
        forceUpdate()
    };

    // const keyMapping: {[key:string]: (navigatePage?: boolean | undefined) => Promise<void>} = {
    //     'ArrowLeft': goBackward,
    //     'ArrowRight': goForward
    // };

    // window.onkeydown = (e: KeyboardEvent) => {
    //     const action = keyMapping[e.key];
    //     if (action) {
    //         action(e.ctrlKey);
    //     }
    // };

    const onChangeSelectedFilter = (value?: string) => {
        setSelectedFilter(value);
    };


    useEffect(() => {
        if (props.groupBy) {

            loadInfraccionesGroup({
                day: params.day ? parseInt(params.day) : undefined,
                equipo: params.equipo && params.unidadProduccion ? { nombre: params.equipo, unidadProduccion: params.unidadProduccion } : undefined,
                month: params.month ? parseInt(params.month) : undefined,
                year: params.year ? parseInt(params.year) : undefined
            });
        }
        else {
            loadInfracciones({ p: 1 });
        }
    }, [params, selectedFilter]);


    return (
        <Container >

            {/* <Header>
                <p>{props.title} {day && (response?.data?.["@odata.count"] ?? 0) > 0 && <span>- <span>{getGlobalIndex()}</span>/<span>{response?.data?.["@odata.count"]}</span></span>}</p>
                <p>{equipo?.nombre}&nbsp;({equipo?.unidadProduccion})</p>
                {props.getMonthStatistics && <QuickStatistics>
                    <div>
                        <span>{props.yesterdayStatisticsText ?? "Procesadas ayer"}</span><span>{yesterdayStatisticsNumber ?? '-'}</span>
                    </div>
                    <div>
                        <span>{props.todayStatisticsText ?? "Procesadas hoy"}</span><span>{todayStatisticsNumber ?? '-'}</span>
                    </div>
                    {props.statisticsProcess && <Button onClick={() => setShowStatistics(true)}>Ver estadísticas</Button>}
                   
                </QuickStatistics>}
            </Header> */}

            <Header style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', alignItems: 'center', gap: '1rem' }}>
                {/* Columna Izquierda */}
                <div>
                    <p style={{ margin: 0 }}>
                        {props.title}
                        {day && (response?.data?.["@odata.count"] ?? 0) > 0 &&
                            <span>- <span>{getGlobalIndex()}</span>/<span>{response?.data?.["@odata.count"]}</span></span>
                        }
                    </p>
                </div>

                {/* Columna Centro */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <p style={{ margin: 0 }}>
                        {equipo?.nombre}&nbsp;({equipo?.unidadProduccion})
                    </p>

                    {/* Lugar de infracción en segundo renglón */}
                    {lugarInfreaccion && (
                        <div style={{ marginTop: '0.25rem' }}>
                            {lugarInfreaccion}
                        </div>
                    )}
                </div>

                {/* Columna Derecha */}
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {props.getMonthStatistics &&
                        <QuickStatistics>
                            <div>
                                <span>{props.yesterdayStatisticsText ?? "Procesadas ayer"}</span>
                                <span>{yesterdayStatisticsNumber ?? '-'}</span>
                            </div>
                            <div>
                                <span>{props.todayStatisticsText ?? "Procesadas hoy"}</span>
                                <span>{todayStatisticsNumber ?? '-'}</span>
                            </div>
                            {props.statisticsProcess &&
                                <Button onClick={() => setShowStatistics(true)}>Ver estadísticas</Button>
                            }
                        </QuickStatistics>
                    }
                </div>
            </Header>

            {props.statusFilters && <Filters>
                <Form.Item label="Estado:">
                    <Select value={selectedFilter} onChange={onChangeSelectedFilter} allowClear>
                        {props.statusFilters?.map((s, index) => <Select.Option key={index} value={s.label}>{s.label}</Select.Option>)}
                    </Select>
                </Form.Item>
            </Filters>}

            {props.statisticsProcess && <Modal onCancel={() => setShowStatistics(false)} footer={[]} width={1000} visible={showStatistics} title="Estadísticas">
                <EstadisticasContent
                    hideProcessFilter
                    hideUPFilter
                    hideEquipoFilter
                    hideRenombradorFilter
                    initialFilters={{
                        Proceso: props.statisticsProcess
                    }}
                />
            </Modal>}

            {props.breadcrumbUrl && equipo &&
                <Breadcrumb>
                    <div onClick={() => navigate(`${props.breadcrumbUrl}`)}>Inicio <RightOutlined /></div>
                    {equipo && <div onClick={() => navigate(`${props.breadcrumbUrl}/${equipo.unidadProduccion}/${equipo.nombre}/`)}>{equipo.nombre} ({equipo.unidadProduccion}) <RightOutlined /></div>}
                    {year && <div onClick={() => navigate(`${props.breadcrumbUrl}/${equipo!.unidadProduccion}/${equipo!.nombre}/${year}`)}>{year} <RightOutlined /></div>}
                    {month && <div onClick={() => navigate(`${props.breadcrumbUrl}/${equipo!.unidadProduccion}/${equipo!.nombre}/${year}/${month}`)}>{month} <RightOutlined /></div>}
                    {day && <div >{day}</div>}
                </Breadcrumb>}

            {(day || !props.groupBy) &&
                <PaginationContainer>
                    <Button disabled={page == 1 && index == 0} onClick={() => goBackward()} type="text" icon={<CaretLeftOutlined />} />
                    <PaginationItems>
                        {infracciones?.map((infraccion, idx) =>
                            <div key={`c-${infraccion.Id}`}>
                                {!loadingIndexes[idx] && <PaginationItem key={`item-${infraccion.Id}`} style={{ backgroundColor: props?.getItemStatusColor(index, page, infraccion) }} onClick={() => setIndex(idx)} selected={idx == index} />}
                                {loadingIndexes[idx] && <StyledItemSpin key={`item-${infraccion.Id}`} size="small" />}
                            </div>)}
                    </PaginationItems>
                    <Button disabled={response?.data?.["@odata.count"] == 0 || page == getTotalPages() && index + 1 == response?.data?.value.length} onClick={() => goForward()} type="text" icon={<CaretRightOutlined />} />
                </PaginationContainer>
            }
            <Body>

                {loading && !response && <StyledSpinFullscreen size="large" />}
                {loading && response && <StyledSpin size="large" />}

                {!day && props.groupBy && (groupResponse?.data?.value.length ?? 0) > 0 &&
                    <FoldersContainer>
                        {groupResponse?.data?.value.map((g) =>
                            <>
                                {!equipo &&
                                    <Folder
                                        quantity={g.cantidadInfracciones}
                                        progress={props.getProgress?.(g)}
                                        progressQuantity={props.getProgressQuantity?.(g)}
                                        title={`${g.equipoNombre} (${g.equipoUnidadProduccion})`}
                                        url={`${g.equipoUnidadProduccion}/${g.equipoNombre}`}
                                        key={`folder-equipo-${g.equipoNombre}-${g.equipoUnidadProduccion}`}
                                    />
                                }
                                {!year && g.anio != 0 &&
                                    <Folder
                                        quantity={g.cantidadInfracciones}
                                        progress={props.getProgress?.(g)}
                                        progressQuantity={props.getProgressQuantity?.(g)}
                                        title={`Año ${g.anio}`}
                                        url={`${g.anio}`}
                                        key={`folder-anio-${g.anio}`}
                                    />}
                                {!month && g.mes != 0 &&

                                    <Folder
                                        quantity={g.cantidadInfracciones}
                                        progress={props.getProgress?.(g)}
                                        progressQuantity={props.getProgressQuantity?.(g)}
                                        title={`Mes ${g.mes}`}
                                        url={`${g.mes}`}
                                        key={`folder-mes-${g.mes}`}
                                    />}
                                {!day && g.dia != 0 &&
                                    <Folder
                                        quantity={g.cantidadInfracciones}
                                        progress={props.getProgress?.(g)}
                                        progressQuantity={props.getProgressQuantity?.(g)}
                                        title={`Día ${g.dia}`}
                                        url={`${g.dia}`}
                                        key={`folder-dia-${g.dia}`}
                                    />}
                            </>)
                        }
                    </FoldersContainer>}

                {day && infracciones?.slice(index, maxImagesPreload + index).map((infraccion, idx) =>
                    <InfraccionContainer key={`infraccion-${infraccion.Id}`} className={0 == idx ? 'visible' : ''}>
                        <InfraccionesBrowserContext.Provider value={{ incrementTodayStatisticsValue: () => setTodayStatisticsNumber((todayStatisticsNumber ?? 0) + 1), infraccion, queue, refreshInfraccion, setLoadingCurrentIndex, loadInfracciones, goForward, goBackward }}>
                            {props.children}
                        </InfraccionesBrowserContext.Provider>
                    </InfraccionContainer>
                )}
                {((day && response?.data?.["@odata.count"] == 0) || groupResponse?.data?.value.length == 0) && <EmptyMessage>No hay infracciones disponibles</EmptyMessage>}
            </Body>
        </Container>
    );
};